import { graphql } from 'react-relay';
import React, { createContext, useState, useCallback, useEffect } from 'react';
import type { FC } from 'react';
import isEqual from 'lodash/isEqual';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import type { RetrieveEditorTitleAndContent } from '@confluence/editor-features/entry-points/useRetrieveEditorTitleAndContent.ts';
import type { PublishProps } from '@confluence/editor-features/entry-points/types.ts';
import type { OverrideMovePageParams } from '@confluence/page-tree/entry-points/useMovePageHandler.ts';
import { usePubSubEvent } from '@confluence/pubsub-client/entry-points/usePubSubEvent.ts';
import { useFetchRestrictionsRESTObj } from '@confluence/restrictions/entry-points/RestrictionsREST.ts';
import type { RestrictionsRESTObj } from '@confluence/restrictions/entry-points/RestrictionsREST.ts';
import {
	restrictionsArePrivateToYouOnlyGql,
	type GqlRestrictionsInput,
} from '@confluence/restrictions/entry-points/convertRestrictionsToGqlRestrictionsInput.ts';
import { useApprovals } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import { fg } from '@confluence/feature-gating';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useDoesTargetSpaceHaveDisabledBlogs } from '@confluence/move-blog-modal/entry-points/useDoesTargetSpaceHaveDisabledBlogs.ts';
import type {
	PrincipalsMap,
	GeneralAccessLevel,
} from '@confluence/share-and-restrict-dialog/entry-points/commonTypes.ts';

import { usePublishModalMountStore } from './PublishModalMountState.tsx';
import type { PublishModalSource } from './PublishModalMountState.tsx';
import type {
	PublishModalContextPublishModalWithMoreOptionsQuery as PublishModalContextPublishModalWithMoreOptionsQuery$data,
	PublishModalContextPublishModalWithMoreOptionsQueryVariables as PublishModalContextPublishModalWithMoreOptionsQuery$variables,
} from './__types__/PublishModalContextPublishModalWithMoreOptionsQuery.ts';
import {
	convertRestrictionsObjFromSchedulePublishInfo,
	convertGqlRestrictionsInputFromSchedulePublishInfo,
} from './publishHelpers.tsx';
// These properties are the parts of the publish modal's state
// that represent the new settings the user picked to be applied
// to the page upon publish.
export type PublishModalContextSettings = {
	pendingScheduledPublishDate: Date | undefined;
	versionComments: string;
	publishAsBlog: boolean;
	shouldNotify: boolean;
	moveParams: OverrideMovePageParams | undefined;
	restrictionsObj: RestrictionsRESTObj | undefined;
	gqlRestrictionsInput: GqlRestrictionsInput | undefined;
	publishModalPrincipals: PrincipalsMap | undefined;
	publishModalGeneralAccess: GeneralAccessLevel | undefined;
};
const DEFAULT_SETTINGS: PublishModalContextSettings = {
	pendingScheduledPublishDate: undefined,
	versionComments: '',
	publishAsBlog: false,
	shouldNotify: true,
	moveParams: undefined,
	restrictionsObj: undefined,
	gqlRestrictionsInput: undefined,
	publishModalPrincipals: undefined,
	publishModalGeneralAccess: undefined,
};

export type PublishModalContextType = PublishModalContextSettings & {
	currentlyScheduledPublishDate: Date | undefined;
	setCurrentlyScheduledPublishDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
	setPendingScheduledPublishDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
	scheduledPublishDate: Date | undefined;
	setVersionComments: React.Dispatch<React.SetStateAction<string>>;
	setPublishAsBlog: React.Dispatch<React.SetStateAction<boolean>>;
	expanded: boolean;
	setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
	setShouldNotify: React.Dispatch<React.SetStateAction<boolean>>;
	setRestrictionsObj: React.Dispatch<React.SetStateAction<RestrictionsRESTObj | undefined>>;
	setGqlRestrictionsInput: React.Dispatch<React.SetStateAction<GqlRestrictionsInput | undefined>>;
	setPublishModalPrincipals: React.Dispatch<React.SetStateAction<PrincipalsMap | undefined>>;
	setPublishModalGeneralAccess: React.Dispatch<
		React.SetStateAction<GeneralAccessLevel | undefined>
	>;
	hasModifiedRestrictions: boolean;
	refetchAll: () => void;
	shouldNotifyComputedValue: boolean;
	discardCurrentState: () => void;
	graphqlError: ApolloErrorInterop | undefined;
	restrictionsGraphQLError: Error | undefined;
	/** True while the restrictions query is in flight (initial fetch and refetch on reopen). */
	isRestrictionsLoading: boolean;
	onClickPublish: (arg: PublishProps) => any;
	onClickPreview: (...args: any) => any;
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
	flags: FlagsStateContainer;
	hasModifiedSettings: boolean;
	isBlog: boolean;
	setIsBlog: React.Dispatch<React.SetStateAction<boolean>>;
	setMoveParams: React.Dispatch<React.SetStateAction<OverrideMovePageParams | undefined>>;
	moveParams: OverrideMovePageParams | undefined;
	hasModifiedLocation: boolean;
	hasScheduledRestrictions: boolean;
	setHasScheduledRestrictions: React.Dispatch<React.SetStateAction<boolean>>;
	isConvertToPageDialog: boolean;
	modalSource?: PublishModalSource;
	targetSpaceHasBlogsDisabled: boolean;
	isUserSpaceAdminOfTargetSpace: boolean;
	blogTargetSpaceKey: string | undefined;
	isTargetSpaceBlogCheckLoading: boolean;
};

export const defaultRetrieveEditorTitleAndContent: RetrieveEditorTitleAndContent = () =>
	Promise.resolve({
		title: '',
		adf: { type: '' },
	});

// These default values are never used, but we have to provide them in case
// a component decides to consume PublishModalContext without a parent providing the context.
export const PublishModalContext: React.Context<PublishModalContextType> =
	createContext<PublishModalContextType>({
		...DEFAULT_SETTINGS,
		currentlyScheduledPublishDate: undefined,
		setCurrentlyScheduledPublishDate: () => {},
		setPendingScheduledPublishDate: () => {},
		scheduledPublishDate: undefined,
		setVersionComments: () => {},
		setPublishAsBlog: () => {},
		expanded: false,
		setExpanded: () => {},
		setShouldNotify: () => {},
		setRestrictionsObj: () => {},
		setGqlRestrictionsInput: () => {},
		setPublishModalPrincipals: () => {},
		setPublishModalGeneralAccess: () => {},
		hasModifiedRestrictions: false,
		refetchAll: () => {},
		shouldNotifyComputedValue: true,
		discardCurrentState: () => {},
		graphqlError: undefined,
		restrictionsGraphQLError: undefined,
		isRestrictionsLoading: false,
		onClickPublish: () => {},
		onClickPreview: () => {},
		retrieveEditorTitleAndContent: defaultRetrieveEditorTitleAndContent,
		flags: new FlagsStateContainer([]),
		hasModifiedSettings: true,
		isBlog: false,
		setIsBlog: () => {},
		setMoveParams: () => {},
		hasModifiedLocation: false,
		hasScheduledRestrictions: false,
		setHasScheduledRestrictions: () => {},
		isConvertToPageDialog: false,
		modalSource: undefined,
		targetSpaceHasBlogsDisabled: false,
		isUserSpaceAdminOfTargetSpace: false,
		blogTargetSpaceKey: undefined,
		isTargetSpaceBlogCheckLoading: false,
	});

export const usePublishModalContextStates = (): {
	currentlyScheduledPublishDate: Date | undefined;
	setCurrentlyScheduledPublishDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
	pendingScheduledPublishDate: Date | undefined;
	setPendingScheduledPublishDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
	versionComments: string;
	setVersionComments: React.Dispatch<React.SetStateAction<string>>;
	publishAsBlog: boolean;
	setPublishAsBlog: React.Dispatch<React.SetStateAction<boolean>>;
	expanded: boolean;
	setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
	shouldNotify: boolean;
	setShouldNotify: React.Dispatch<React.SetStateAction<boolean>>;
	restrictionsObj: RestrictionsRESTObj | undefined;
	setRestrictionsObj: React.Dispatch<React.SetStateAction<RestrictionsRESTObj | undefined>>;
	gqlRestrictionsInput: GqlRestrictionsInput | undefined;
	setGqlRestrictionsInput: React.Dispatch<React.SetStateAction<GqlRestrictionsInput | undefined>>;
	publishModalPrincipals: PrincipalsMap | undefined;
	setPublishModalPrincipals: React.Dispatch<React.SetStateAction<PrincipalsMap | undefined>>;
	publishModalGeneralAccess: GeneralAccessLevel | undefined;
	setPublishModalGeneralAccess: React.Dispatch<
		React.SetStateAction<GeneralAccessLevel | undefined>
	>;
	hasModifiedRestrictions: boolean;
	scheduledPublishDate: Date | undefined;
	discardCurrentState: () => void;
	savedSnapshot: PublishModalContextSettings;
	setSavedSnapshot: React.Dispatch<React.SetStateAction<PublishModalContextSettings>>;
	hasModifiedSettings: boolean;
	isBlog: boolean;
	setIsBlog: React.Dispatch<React.SetStateAction<boolean>>;
	setMoveParams: React.Dispatch<React.SetStateAction<OverrideMovePageParams | undefined>>;
	moveParams: OverrideMovePageParams | undefined;
	hasModifiedLocation: boolean;
	hasScheduledRestrictions: boolean;
	setHasScheduledRestrictions: React.Dispatch<React.SetStateAction<boolean>>;
} => {
	const [currentlyScheduledPublishDate, setCurrentlyScheduledPublishDate] = useState<
		Date | undefined
	>(undefined);
	const [pendingScheduledPublishDate, setPendingScheduledPublishDate] = useState<Date | undefined>(
		DEFAULT_SETTINGS.pendingScheduledPublishDate,
	);
	const [versionComments, setVersionComments] = useState<string>(DEFAULT_SETTINGS.versionComments);
	const [publishAsBlog, setPublishAsBlog] = useState<boolean>(DEFAULT_SETTINGS.publishAsBlog);
	const [expanded, setExpanded] = useState<boolean>(false);
	const [shouldNotify, setShouldNotify] = useState<boolean>(DEFAULT_SETTINGS.shouldNotify);
	// undefined indicates that no user-initiated changes have been mode to restrictions
	const [restrictionsObj, setRestrictionsObj] = useState<RestrictionsRESTObj | undefined>(
		DEFAULT_SETTINGS.restrictionsObj,
	);
	const [gqlRestrictionsInput, setGqlRestrictionsInput] = useState<
		GqlRestrictionsInput | undefined
	>(DEFAULT_SETTINGS.gqlRestrictionsInput);
	const [publishModalPrincipals, setPublishModalPrincipals] = useState<PrincipalsMap | undefined>(
		DEFAULT_SETTINGS.publishModalPrincipals,
	);
	const [publishModalGeneralAccess, setPublishModalGeneralAccess] = useState<
		GeneralAccessLevel | undefined
	>(DEFAULT_SETTINGS.publishModalGeneralAccess);
	const [savedSnapshot, setSavedSnapshot] = useState<PublishModalContextSettings>(DEFAULT_SETTINGS);
	const [hasScheduledRestrictions, setHasScheduledRestrictions] = useState<boolean>(false);
	const [isBlog, setIsBlog] = useState<boolean>(false);
	const [moveParams, setMoveParams] = useState<OverrideMovePageParams | undefined>(
		DEFAULT_SETTINGS.moveParams,
	);

	// When the publish modal opens and a date is already scheduled on the backend,
	// the pendingScheduledPublishDate is set to undefined to indicate there is
	// no date picked by the user, while the currentlyScheduledPublishDate will
	// have the actualy scheduled date value.
	// This check prevents that situation from triggering the discard dialog.
	const hasModifiedDate =
		currentlyScheduledPublishDate?.getTime() !== pendingScheduledPublishDate?.getTime() &&
		pendingScheduledPublishDate !== undefined;
	const hasModifiedRestrictions =
		gqlRestrictionsInput !== undefined &&
		!isEqual(savedSnapshot.gqlRestrictionsInput, gqlRestrictionsInput);
	const hasModifiedLocation =
		savedSnapshot.publishAsBlog !== publishAsBlog || savedSnapshot.moveParams !== moveParams;
	const hasModifiedSettings =
		hasModifiedDate ||
		savedSnapshot.versionComments !== versionComments ||
		savedSnapshot.shouldNotify !== shouldNotify ||
		hasModifiedLocation ||
		hasModifiedRestrictions;

	/* Function to discard current state on modal close */
	const discardCurrentState = (): void => {
		setPendingScheduledPublishDate(DEFAULT_SETTINGS.pendingScheduledPublishDate);
		setVersionComments(DEFAULT_SETTINGS.versionComments);
		setPublishAsBlog(DEFAULT_SETTINGS.publishAsBlog);
		setShouldNotify(DEFAULT_SETTINGS.shouldNotify);
		setMoveParams(DEFAULT_SETTINGS.moveParams);
		setRestrictionsObj(DEFAULT_SETTINGS.restrictionsObj);
		setGqlRestrictionsInput(DEFAULT_SETTINGS.gqlRestrictionsInput);
		setPublishModalPrincipals(DEFAULT_SETTINGS.publishModalPrincipals);
		setPublishModalGeneralAccess(DEFAULT_SETTINGS.publishModalGeneralAccess);
		setSavedSnapshot((prev) => ({
			...DEFAULT_SETTINGS,
			restrictionsObj: prev.restrictionsObj,
			gqlRestrictionsInput: prev.gqlRestrictionsInput,
		}));
	};

	const scheduledPublishDate = pendingScheduledPublishDate ?? currentlyScheduledPublishDate;

	return {
		currentlyScheduledPublishDate,
		setCurrentlyScheduledPublishDate,
		pendingScheduledPublishDate,
		setPendingScheduledPublishDate,
		versionComments,
		setVersionComments,
		publishAsBlog,
		setPublishAsBlog,
		expanded,
		setExpanded,
		shouldNotify,
		setShouldNotify,
		restrictionsObj,
		setRestrictionsObj,
		gqlRestrictionsInput,
		setGqlRestrictionsInput,
		publishModalPrincipals,
		setPublishModalPrincipals,
		publishModalGeneralAccess,
		setPublishModalGeneralAccess,
		hasModifiedRestrictions,
		scheduledPublishDate,
		discardCurrentState,
		savedSnapshot,
		setSavedSnapshot,
		hasModifiedSettings,
		isBlog,
		setIsBlog,
		setMoveParams,
		moveParams,
		hasModifiedLocation,
		hasScheduledRestrictions,
		setHasScheduledRestrictions,
	};
};

type PublishModalContextProviderProps = {
	children: React.ReactNode;
	onClickPublish: (arg: PublishProps) => any;
	onClickPreview: (...args: any) => any;
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
	flags: FlagsStateContainer;
	isUnpublishedDraft: boolean;
	contentType: string;
	isConvertToPageDialog?: boolean;
};
export const PublishModalContextProvider: React.ComponentType<
	Omit<PublishModalContextProviderProps, 'flags'>
> = withFlags(
	({
		children,
		onClickPublish,
		onClickPreview,
		retrieveEditorTitleAndContent,
		flags,
		isUnpublishedDraft,
		contentType,
		isConvertToPageDialog = false,
	}: PublishModalContextProviderProps) => {
		const [contentId] = usePageContentId();
		const [currentSpaceKey] = usePageSpaceKey();
		const { modalSource } = usePublishModalMountStore();

		/**
		 * Live-doc → page approval flow: when the user initiated this publish flow from the
		 * live-doc Start Approval popup, force convert-to-page mode for the dialog regardless
		 * of what the caller passed. The intent lives on ApprovalsContext (the only place all
		 * three of approvals/publish-modal/content-state already share without creating a
		 * cycle).
		 *
		 */
		const { pendingLiveDocApprovalContentId } = useApprovals();
		const effectiveIsConvertToPageDialog =
			isConvertToPageDialog ||
			(Boolean(pendingLiveDocApprovalContentId) &&
				expValEquals('confluence_approvals_eap', 'isEnabled', true));

		/* State declarations */
		const {
			currentlyScheduledPublishDate,
			setCurrentlyScheduledPublishDate,
			pendingScheduledPublishDate,
			setPendingScheduledPublishDate,
			versionComments,
			setVersionComments,
			publishAsBlog,
			setPublishAsBlog,
			expanded,
			setExpanded,
			shouldNotify,
			setShouldNotify,
			restrictionsObj,
			setRestrictionsObj,
			gqlRestrictionsInput,
			setGqlRestrictionsInput,
			publishModalPrincipals,
			setPublishModalPrincipals,
			publishModalGeneralAccess,
			setPublishModalGeneralAccess,
			hasModifiedRestrictions,
			scheduledPublishDate,
			discardCurrentState,
			savedSnapshot,
			setSavedSnapshot,
			hasModifiedSettings,
			isBlog,
			setIsBlog,
			setMoveParams,
			moveParams,
			hasModifiedLocation,
			hasScheduledRestrictions,
			setHasScheduledRestrictions,
		} = usePublishModalContextStates();

		const {
			getSpaceSidebarWebItems,
			spaceSidebarWebItemsLoading,
			spaceSidebarWebItemsError,
			selectedSpaceHasBlogsToggledOff,
			isUserSpaceAdminOfTargetSpace,
		} = useDoesTargetSpaceHaveDisabledBlogs();

		const blogTargetSpaceKey = publishAsBlog
			? (moveParams?.newSpaceKey ?? (!isBlog ? currentSpaceKey : undefined))
			: undefined;

		useEffect(() => {
			if (blogTargetSpaceKey) {
				void getSpaceSidebarWebItems({ variables: { spaceKey: blogTargetSpaceKey } });
			}
			// getSpaceSidebarWebItems is a stable lazy-query trigger; re-run only when the target changes.
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [blogTargetSpaceKey]);

		const targetSpaceHasBlogsDisabled = Boolean(
			blogTargetSpaceKey &&
			// Fail closed if the blogs-enabled check errors.
			(selectedSpaceHasBlogsToggledOff || spaceSidebarWebItemsError) &&
			expVal('cc_perms_block_publish_blog_to_disabled_space', 'isEnabled', false),
		);

		const isTargetSpaceBlogCheckLoading = Boolean(
			blogTargetSpaceKey && spaceSidebarWebItemsLoading,
		);

		const onSchedulePublishPubsubEvent = useCallback(
			(_: any, payload: any) => {
				if (
					!['DUAL', 'GRAPHQL'].includes(
						StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
					)
				) {
					const {
						schedulePublishRequestBody: { publishTime, eventType },
					} = payload;
					if (eventType === 'scheduled') {
						if (
							!currentlyScheduledPublishDate ||
							currentlyScheduledPublishDate?.toISOString() !== publishTime
						) {
							setCurrentlyScheduledPublishDate(new Date(publishTime));
						}
					} else if (eventType === 'unscheduled') {
						if (currentlyScheduledPublishDate) {
							setCurrentlyScheduledPublishDate(undefined);
						}
					}
				}
			},
			[currentlyScheduledPublishDate, setCurrentlyScheduledPublishDate],
		);

		const onSchedulePublishSubscriptionEvent = useCallback(
			(_delta: string, response: any) => {
				const { eventType, publishTime } = response?.schedulePublished || {};

				if (eventType === 'SCHEDULED') {
					if (
						!currentlyScheduledPublishDate ||
						currentlyScheduledPublishDate?.toISOString() !== publishTime
					) {
						setCurrentlyScheduledPublishDate(new Date(publishTime));
					}
				} else if (eventType === 'UNSCHEDULED') {
					if (currentlyScheduledPublishDate) {
						setCurrentlyScheduledPublishDate(undefined);
					}
				}
			},
			[currentlyScheduledPublishDate, setCurrentlyScheduledPublishDate],
		);

		usePubSubEvent({
			contentId: contentId ?? '',
			contentType: contentType ?? 'page',
			eventName: contentType === 'blogpost' ? 'schedulePublish:blogpost' : 'schedulePublish:page',
			onEvent: onSchedulePublishPubsubEvent,
			skip:
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				fg('confluence_enable_subscriptions-context-provider') &&
				StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', '') ===
					'GRAPHQL',
		});

		useSubscriptionEvent({
			contentId: contentId ?? '',
			contentType: contentType ?? 'page',
			subscriberId: 'PublishModalContextSubscription',
			delta: 'schedulePublished',
			skip:
				// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
				!fg('confluence_enable_subscriptions-context-provider') ||
				!['DUAL', 'GRAPHQL'].includes(
					StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
				),
			onDataReceived: onSchedulePublishSubscriptionEvent,
		});

		// Memoized so the fetch hook's data-effect keeps a stable identity behind the gate; an
		// unstable callback would re-run that effect every render and loop on setSavedSnapshot.
		const onFetchRestrictionsSuccess = useCallback(
			(newRestrictions: RestrictionsRESTObj) => {
				// Snapshot should match scheduled restrictions instead of true restrictions
				if (hasScheduledRestrictions) {
					return;
				}
				setSavedSnapshot((prev) => ({
					...prev,
					restrictionsObj: newRestrictions,
				}));
			},
			[hasScheduledRestrictions, setSavedSnapshot],
		);

		const onFetchRestrictionsSuccessGql = useCallback(
			(newGqlRestrictions: GqlRestrictionsInput) => {
				if (hasScheduledRestrictions) {
					return;
				}
				setSavedSnapshot((prev) => ({
					...prev,
					gqlRestrictionsInput: newGqlRestrictions,
				}));
			},
			[hasScheduledRestrictions, setSavedSnapshot],
		);

		const {
			error: restrictionsGraphQLError,
			refetch: refetchRestrictions,
			loading: isRestrictionsLoading,
		} = useFetchRestrictionsRESTObj({
			contentId: contentId ?? '',
			onSuccess: onFetchRestrictionsSuccess,
			onSuccessGql: onFetchRestrictionsSuccessGql,
		});

		const isFirstTimeBlogPost = isUnpublishedDraft && isBlog;
		let shouldNotifyComputedValue = shouldNotify;
		const computedRestrictions = hasModifiedRestrictions
			? restrictionsObj
			: savedSnapshot.restrictionsObj;
		const computedGqlRestrictionsInput = hasModifiedRestrictions
			? gqlRestrictionsInput
			: savedSnapshot.gqlRestrictionsInput;
		const isPrivateToYouOnly =
			computedGqlRestrictionsInput &&
			restrictionsArePrivateToYouOnlyGql(computedGqlRestrictionsInput);
		if (isPrivateToYouOnly) {
			shouldNotifyComputedValue = false;
		} else if (publishAsBlog || isFirstTimeBlogPost) {
			shouldNotifyComputedValue = true;
		}

		const loadQueryDataIntoPublishModalState = (newQueryData) => {
			let nextCurrentlyScheduledPublishDate;
			let nextPendingScheduledPublishDate;
			let nextExpanded = expanded;
			let nextShouldNotify;
			let nextPublishAsBlog = publishAsBlog;
			let nextRestrictionsRestObj = restrictionsObj;
			let nextGqlRestrictionsInput = gqlRestrictionsInput;
			let nextmoveParams = moveParams;

			const schedulePublishInfo = newQueryData?.singleContent?.schedulePublishInfo;
			if (schedulePublishInfo?.date) {
				nextCurrentlyScheduledPublishDate = new Date(Date.parse(schedulePublishInfo?.date));
				nextPendingScheduledPublishDate = undefined;
				nextExpanded = true;
			} else {
				nextCurrentlyScheduledPublishDate = undefined;
				nextPendingScheduledPublishDate = undefined;
			}

			const nextVersionComments = schedulePublishInfo?.versionComment ?? '';

			if (typeof schedulePublishInfo?.minorEdit === 'boolean') {
				// if minorEdit==false then we should notify
				nextShouldNotify = !schedulePublishInfo?.minorEdit;
			} else {
				// if we don't have a value for this, set to true which is the default
				nextShouldNotify = true;
			}

			nextPublishAsBlog =
				schedulePublishInfo?.targetType === 'blogpost' &&
				newQueryData?.singleContent?.type !== 'blogpost';

			const targetLocation = schedulePublishInfo?.targetLocation;
			if (targetLocation?.destinationSpace?.key) {
				nextmoveParams = {
					newSpaceKey:
						targetLocation?.destinationSpace?.alias || targetLocation?.destinationSpace?.key,
				};
				if (targetLocation?.parentId) {
					nextmoveParams = {
						...nextmoveParams,
						newParentPageId: targetLocation?.parentId,
					};
				}
			}

			if (schedulePublishInfo?.restrictions) {
				setHasScheduledRestrictions(true);
				nextRestrictionsRestObj = convertRestrictionsObjFromSchedulePublishInfo(
					schedulePublishInfo?.restrictions,
				);
				nextGqlRestrictionsInput = convertGqlRestrictionsInputFromSchedulePublishInfo(
					schedulePublishInfo?.restrictions,
				);
			}

			setCurrentlyScheduledPublishDate(nextCurrentlyScheduledPublishDate);
			setPendingScheduledPublishDate(nextPendingScheduledPublishDate);
			setExpanded(nextExpanded);
			setVersionComments(nextVersionComments);
			setShouldNotify(nextShouldNotify);
			setPublishAsBlog(nextPublishAsBlog);
			setRestrictionsObj(nextRestrictionsRestObj);
			setGqlRestrictionsInput(nextGqlRestrictionsInput);
			setMoveParams(nextmoveParams);

			// TODO: GENI-2751 - Read staged locations from schedule publish info
			// and stage to snapshot with moveParams
			setSavedSnapshot((oldSavedSnapshot) => ({
				pendingScheduledPublishDate: nextPendingScheduledPublishDate,
				versionComments: nextVersionComments,
				publishAsBlog: nextPublishAsBlog,
				shouldNotify: nextShouldNotify,
				moveParams: nextmoveParams,
				// If savedSnapshot.restrictionsObj has just been set by useFetchRestrictionsRESTObj, we need to not override what it just set.
				restrictionsObj: nextRestrictionsRestObj ?? oldSavedSnapshot.restrictionsObj,
				// If savedSnapshot.gqlRestrictionsInput has just been set by useFetchRestrictionsRESTObj, we need to not override what it just set.
				gqlRestrictionsInput: nextGqlRestrictionsInput ?? oldSavedSnapshot.gqlRestrictionsInput,
				// Preserve staged principals and general access
				publishModalPrincipals: oldSavedSnapshot.publishModalPrincipals,
				publishModalGeneralAccess: oldSavedSnapshot.publishModalGeneralAccess,
			}));
		};

		/* Fetching already scheduled publish data */
		const {
			data: queryData,
			error: graphqlError,
			refetch: refetchPublishModalQuery,
		} = useQueryInterop<
			PublishModalContextPublishModalWithMoreOptionsQuery$data,
			PublishModalContextPublishModalWithMoreOptionsQuery$variables
		>(
			graphql`
				query PublishModalContextPublishModalWithMoreOptionsQuery($contentId: ID) {
					singleContent(id: $contentId) {
						id
						type
						space {
							id
						}
						schedulePublishInfo {
							date
							versionComment
							minorEdit
							targetType
							targetLocation {
								parentId
								destinationSpace {
									id
									key
									alias
								}
							}
							restrictions {
								update {
									user: personConnection {
										edges {
											node {
												email
												permissionType
												displayName
												accountId
												publicName
												profilePicture {
													path
												}
											}
										}
									}
									group {
										edges {
											node {
												name
												id
											}
										}
									}
								}
								read {
									user: personConnection {
										edges {
											node {
												email
												permissionType
												displayName
												accountId
												publicName
												profilePicture {
													path
												}
											}
										}
									}
									group {
										edges {
											node {
												name
												id
											}
										}
									}
								}
							}
						}
					}
				}
			`,
			{
				errorPolicy: 'all',
				fetchPolicy: 'cache-and-network',
				variables: {
					contentId,
				},
				onCompleted: loadQueryDataIntoPublishModalState,
			},
		);

		useEffect(() => {
			const schedulePublishDateFromQuery = queryData?.singleContent?.schedulePublishInfo?.date;
			if (schedulePublishDateFromQuery) {
				const newSchedulePublishDateFromQuery = new Date(Date.parse(schedulePublishDateFromQuery));
				setCurrentlyScheduledPublishDate(newSchedulePublishDateFromQuery);
			}
		}, [queryData?.singleContent?.schedulePublishInfo?.date, setCurrentlyScheduledPublishDate]);

		const resetToInitialState = () => {
			if (queryData?.singleContent?.schedulePublishInfo?.date) {
				// If this page is currently scheduled to be published,
				// then load the settings of the current scheduled publish from the backend.
				loadQueryDataIntoPublishModalState(queryData);
			} else {
				discardCurrentState();
			}
		};

		const refetchAll = useCallback(() => {
			void refetchPublishModalQuery();
			void refetchRestrictions();
		}, [refetchPublishModalQuery, refetchRestrictions]);

		/* Objects that are provided via the context */
		const contextProvidedData = {
			currentlyScheduledPublishDate,
			setCurrentlyScheduledPublishDate,
			pendingScheduledPublishDate,
			setPendingScheduledPublishDate,
			scheduledPublishDate,
			versionComments,
			setVersionComments,
			publishAsBlog,
			setPublishAsBlog,
			expanded,
			setExpanded,
			shouldNotify,
			setShouldNotify,
			restrictionsObj: computedRestrictions,
			setRestrictionsObj,
			gqlRestrictionsInput: computedGqlRestrictionsInput,
			setGqlRestrictionsInput,
			publishModalPrincipals,
			setPublishModalPrincipals,
			publishModalGeneralAccess,
			setPublishModalGeneralAccess,
			hasModifiedRestrictions,
			refetchAll,
			shouldNotifyComputedValue,
			graphqlError,
			restrictionsGraphQLError,
			isRestrictionsLoading,
			discardCurrentState: resetToInitialState,
			onClickPublish,
			onClickPreview,
			retrieveEditorTitleAndContent,
			flags,
			hasModifiedSettings,
			isBlog,
			setIsBlog,
			setMoveParams,
			moveParams,
			hasModifiedLocation,
			hasScheduledRestrictions,
			setHasScheduledRestrictions,
			isConvertToPageDialog: effectiveIsConvertToPageDialog,
			modalSource: effectiveIsConvertToPageDialog ? modalSource : undefined,
			targetSpaceHasBlogsDisabled,
			isUserSpaceAdminOfTargetSpace,
			blogTargetSpaceKey,
			isTargetSpaceBlogCheckLoading,
		};
		return (
			// oxlint-disable-next-line react/jsx-no-constructed-context-values
			<PublishModalContext.Provider value={contextProvidedData}>
				{children}
			</PublishModalContext.Provider>
		);
	},
);

export type PublishModalContextProviderExternalProps = {
	onClickPublish: (arg: PublishProps) => any;
	onClickPreview: (...args: any) => any;
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
	isUnpublishedDraft: boolean;
	contentType: string;
	isConvertToPageDialog?: boolean;
};

type WrappedComponentRequiredProps = {
	isUnpublishedDraft: boolean;
	contentType: string;
};

export const withPublishModalContextProvider =
	<T extends {}>(
		WrappedComponent: React.ComponentType<T & WrappedComponentRequiredProps>,
	): FC<T & PublishModalContextProviderExternalProps> =>
	({
		onClickPublish,
		onClickPreview,
		retrieveEditorTitleAndContent,
		isUnpublishedDraft,
		contentType,
		isConvertToPageDialog,
		...props
	}) => {
		return (
			<PublishModalContextProvider
				onClickPublish={onClickPublish}
				onClickPreview={onClickPreview}
				retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
				isUnpublishedDraft={isUnpublishedDraft}
				contentType={contentType}
				isConvertToPageDialog={isConvertToPageDialog}
			>
				<WrappedComponent
					isUnpublishedDraft={isUnpublishedDraft}
					contentType={contentType}
					{...(props as unknown as T)}
				/>
			</PublishModalContextProvider>
		);
	};
